import { useState, useCallback, useEffect } from "react";
import useWalletStore from "../store/wallet.store";
import { subscribe } from "../lib/walletEvents";

// Once-per-session "you're running low on Zencredits" toast.
//
// Pages call `checkAndWarn()` after any action that might have spent credits
// (e.g. an AI Tag run, a chat message, a saveBall). The hook returns the
// toast text to render and a dismiss handler.
//
//   const { toastMsg, dismiss, checkAndWarn } = useLowBalanceToast();
//
//   // After a debit-able action:
//   await fetchBalance(); checkAndWarn();
//
//   // In JSX:
//   {toastMsg && <Toast onClose={dismiss}>{toastMsg}</Toast>}
//
// Threshold defaults to 20 ZC. Won't fire twice in the same component lifecycle.
export default function useLowBalanceToast(threshold = 20) {
  const [warned, setWarned] = useState(false);
  const [toastMsg, setToastMsg] = useState("");

  const checkAndWarn = useCallback(() => {
    if (warned) return;
    const balance = useWalletStore.getState().balance;
    const c = balance?.balance_credits ?? null;
    if (c == null) return;
    if (c < threshold && c >= 0) {
      setToastMsg(
        c === 0
          ? "0 Zencredits left — top up to use AI features."
          : `Only ${c} Zencredits left — top up to keep AI features working.`,
      );
      setWarned(true);
      // Auto-dismiss after 6s; user can click × to clear sooner.
      setTimeout(() => setToastMsg(""), 6000);
    }
  }, [warned, threshold]);

  const dismiss = useCallback(() => setToastMsg(""), []);

  // Auto-fire on any wallet_changed event so pages don't need to call
  // checkAndWarn manually after every action — useCreditConfirm already
  // emits the event on confirm, so this hook reacts to all debit-able
  // flows that go through that path.
  useEffect(() => {
    const off = subscribe("wallet_changed", () => {
      // Defer one tick — the wallet store's fetchBalance may not have
      // completed by the time the event fires.
      setTimeout(() => checkAndWarn(), 200);
    });
    return off;
  }, [checkAndWarn]);

  return { toastMsg, dismiss, checkAndWarn };
}
